package com.milta.feature.profile.data.repository

import com.milta.core.cache.LocalCacheStore
import com.milta.core.network.ApiException
import com.milta.feature.profile.data.remote.ProfileApi
import com.milta.feature.profile.data.remote.dto.ConfirmAccountDeletionRequest
import com.milta.feature.profile.data.remote.dto.ConfirmPhoneChangeRequest
import com.milta.feature.profile.data.remote.dto.ProfileResponse
import com.milta.feature.profile.data.remote.dto.RequestPhoneChangeRequest
import com.milta.feature.profile.data.remote.dto.UpdateLanguageRequest
import com.milta.feature.profile.data.remote.dto.toDomain
import com.milta.feature.profile.domain.entity.ProfileEntity
import com.milta.feature.profile.domain.repository.ProfileRepository
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

private const val PROFILE_CACHE_KEY = "cache:profile"

class ProfileRepositoryImpl(
    private val api: ProfileApi,
    private val cacheStore: LocalCacheStore,
) : ProfileRepository {

    // Mode hors ligne — cache-aside, meme principe que RouteRepositoryImpl.getMyRoute : le cache
    // n'est utilise que si le GET echoue sans reponse HTTP du tout (pas ApiException).
    override suspend fun getProfile(): Result<ProfileEntity> = runCatching {
        api.getProfile()
    }.onSuccess {
        cacheStore.putString(PROFILE_CACHE_KEY, Json.encodeToString(it))
    }.recoverCatching { e ->
        if (e is ApiException) throw e
        val cached = cacheStore.getString(PROFILE_CACHE_KEY) ?: throw e
        Json.decodeFromString<ProfileResponse>(cached)
    }.map { it.toDomain() }

    // MIL-49 — no cache-aside here: the backend regenerates a fresh code as soon as the previous
    // one is consumed, so serving a stale cached value offline would show an already-used code.
    override suspend fun getLinkCode(): Result<String> = runCatching {
        api.getLinkCode().code
    }

    override suspend fun updateLanguage(language: String): Result<Unit> = runCatching {
        api.updateLanguage(UpdateLanguageRequest(language = language))
    }

    override suspend fun requestPhoneChange(phoneNumber: String): Result<Unit> = runCatching {
        api.requestPhoneChange(RequestPhoneChangeRequest(phoneNumber = phoneNumber))
    }

    override suspend fun confirmPhoneChange(code: String): Result<Unit> = runCatching {
        api.confirmPhoneChange(ConfirmPhoneChangeRequest(code = code))
    }

    override suspend fun requestAccountDeletion(): Result<Unit> = runCatching {
        api.requestAccountDeletion()
    }

    override suspend fun confirmAccountDeletion(code: String): Result<Unit> = runCatching {
        api.confirmAccountDeletion(ConfirmAccountDeletionRequest(code = code))
    }
}
